// Achievements / trophy room — local-only, persisted to localStorage.
// No network calls. Pure progression tracking for the curious visitor.

const STORAGE_KEY = 'gb4k_achievements_v1';

export type AchievementId =
  | 'first_page'
  | 'visited_all_standard'
  | 'visited_all_experimental'
  | 'found_secret'
  | 'found_agrou'
  | 'visited_karakoto'
  | 'visited_japan_trio'
  | 'visited_hyakki'
  | 'typed_in_typewriter'
  | 'commited_word'
  | 'climbed_all_summits';

export interface Achievement {
  id: AchievementId;
  emoji: string;
  title: string;
  hint: string;
}

export const ALL_ACHIEVEMENTS: Achievement[] = [
  { id: 'first_page',              emoji: '👋', title: 'First Contact',          hint: 'Open any page from the terminal.' },
  { id: 'visited_all_standard',    emoji: '🏆', title: 'Standard Pilgrim',       hint: 'Visit every page in DIR.' },
  { id: 'visited_all_experimental',emoji: '🌀', title: 'Reality Tester',         hint: 'Open every experimental zone module.' },
  { id: 'found_secret',            emoji: '🔑', title: 'Secret Keeper',          hint: 'Find and unlock the secret page.' },
  { id: 'found_agrou',             emoji: '👹', title: 'Speaker of Agrou',       hint: 'Discover a forgotten word.' },
  { id: 'visited_karakoto',        emoji: '🏯', title: '唐琴へようこそ',           hint: 'Visit Karakoto.' },
  { id: 'visited_japan_trio',      emoji: '⛩️', title: 'Tea Ceremony',           hint: 'Visit Karakoto, Typewriter and Hyakki.' },
  { id: 'visited_hyakki',          emoji: '👻', title: 'Saw the Procession',     hint: 'Witness the night parade.' },
  { id: 'typed_in_typewriter',     emoji: '⌨️', title: 'Apprentice Scribe',      hint: 'Type something in the typewriter.' },
  { id: 'commited_word',           emoji: '🖋️', title: 'Inked',                  hint: 'Commit a word with ENTER in the typewriter.' },
  { id: 'climbed_all_summits',     emoji: '🏔️', title: 'High-Altitude Soul',    hint: 'Stand on every summit.' },
];

// Pages required to unlock "Standard Pilgrim". Must match the DIR whitelist.
const STANDARD_PAGES: string[] = [
  'about', 'skills', 'contact', 'galaxy',
  'books', 'summit', 'karakoto', 'rotary', '3causes',
];

// Pages required to unlock "Reality Tester".
const EXPERIMENTAL_PAGES: string[] = [
  'consciousness', 'disconnect', 'dungeon', 'drift', 'simulation',
  'alyce', 'rabbit', 'remember', 'emergence', 'legacy',
  'typewriter', 'hyakki',
];

interface AchievementState {
  unlocked: AchievementId[];
  visitedPages: string[];
}

function load(): AchievementState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { unlocked: [], visitedPages: [] };
    const parsed = JSON.parse(raw) as Partial<AchievementState>;
    return {
      unlocked: Array.isArray(parsed.unlocked) ? parsed.unlocked : [],
      visitedPages: Array.isArray(parsed.visitedPages) ? parsed.visitedPages : [],
    };
  } catch {
    return { unlocked: [], visitedPages: [] };
  }
}

function save(state: AchievementState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage might be disabled — silently ignore.
  }
}

// Listeners for newly-unlocked achievements (used by the Terminal to flash a notification)
type UnlockListener = (achievement: Achievement) => void;
const listeners: UnlockListener[] = [];

export function onAchievementUnlocked(fn: UnlockListener): () => void {
  listeners.push(fn);
  return () => {
    const idx = listeners.indexOf(fn);
    if (idx >= 0) listeners.splice(idx, 1);
  };
}

function emit(id: AchievementId): void {
  const a = ALL_ACHIEVEMENTS.find((x) => x.id === id);
  if (a) listeners.forEach((fn) => fn(a));
}

export function unlock(id: AchievementId): boolean {
  const state = load();
  if (state.unlocked.includes(id)) return false;
  state.unlocked.push(id);
  save(state);
  emit(id);
  return true;
}

export function recordPageVisit(pageId: string): void {
  const state = load();
  if (!state.visitedPages.includes(pageId)) {
    state.visitedPages.push(pageId);
    save(state);
  }

  // Always unlock first_page on any visit.
  if (!state.unlocked.includes('first_page')) unlock('first_page');

  // Page-specific achievements
  if (pageId === 'karakoto') unlock('visited_karakoto');
  if (pageId === 'hyakki') unlock('visited_hyakki');
  if (pageId === 'agrou') unlock('found_agrou');
  if (pageId === 'secret') unlock('found_secret');

  // Composite checks
  const visited = load().visitedPages;
  if (STANDARD_PAGES.every((p) => visited.includes(p))) unlock('visited_all_standard');
  if (EXPERIMENTAL_PAGES.every((p) => visited.includes(p))) unlock('visited_all_experimental');
  if (['karakoto', 'typewriter', 'hyakki'].every((p) => visited.includes(p))) {
    unlock('visited_japan_trio');
  }
}

export function getUnlocked(): AchievementId[] {
  return load().unlocked;
}

export function getVisitedPages(): string[] {
  return load().visitedPages;
}

export function isUnlocked(id: AchievementId): boolean {
  return load().unlocked.includes(id);
}

export function resetAll(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
